import { useEffect, useState, type ReactNode } from 'react';
import type { Gender, Person } from '../api/types';
import { COUNTRIES_FIRST, COUNTRIES_REST, loadMexico, NAME_PATTERN, tidyName, US_STATES } from '../geo/places';
import { Field } from './components';
import type { FieldError } from './fieldErrors';

/** Datos personales tal como se envían a la API. */
export interface PersonDetailsInput {
  givenName: string;
  paternalSurname: string;
  maternalSurname: string;
  birthDate: string | null;
  birthCity: string | null;
  birthState: string | null;
  birthCountry: string | null;
  address: string | null;
  baptized: boolean | null;
  gender: Gender | null;
  mobilePhone: string | null;
  homePhone: string | null;
}

export const genderLabel = (g: Gender | null) => (g === 'femenino' ? 'Femenino' : g === 'masculino' ? 'Masculino' : 'Sin registrar');
const PHONE = /^[\d\s()+.\/-]*$/;
export const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const daysIn = (year: number, month: number) => new Date(year || 2000, month, 0).getDate();

/**
 * Lee y valida los datos del formulario (a prueba de errores). Obligatorios: nombre(s), apellido
 * paterno, apellido materno (o marcar que no tiene), género, día/mes/año de nacimiento, país, estado
 * y ciudad de nacimiento, dirección y si está bautizado. Opcionales: celular y teléfono.
 */
export function readPersonDetails(
  form: FormData,
  opts: { requireAll?: boolean; requireSurname?: boolean; requireGender?: boolean } = {},
): { details: PersonDetailsInput; error: string | null; fields: FieldError[] } {
  const requireAll = opts.requireAll ?? true;
  const requireSurname = opts.requireSurname ?? true;
  const text = (name: string) => String(form.get(name) ?? '').trim().replace(/\s+/g, ' ');
  const baptizedRaw = form.get('baptized');
  const genderRaw = form.get('gender');
  const noMaternal = form.get('noMaternal') === 'on';
  const day = Number(form.get('birthDay') || 0);
  const month = Number(form.get('birthMonth') || 0);
  const year = Number(form.get('birthYear') || 0);
  const birthDate = day && month && year ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
  const details: PersonDetailsInput = {
    givenName: tidyName(text('givenName')),
    paternalSurname: tidyName(text('paternalSurname')),
    maternalSurname: noMaternal ? '' : tidyName(text('maternalSurname')),
    birthDate,
    birthCity: text('birthCity') || null,
    birthState: text('birthState') || null,
    birthCountry: text('birthCountry') || null,
    address: text('address') || null,
    baptized: baptizedRaw === 'si' ? true : baptizedRaw === 'no' ? false : null,
    gender: genderRaw === 'femenino' || genderRaw === 'masculino' ? genderRaw : null,
    mobilePhone: text('mobilePhone') || null,
    homePhone: text('homePhone') || null,
  };
  const fields: FieldError[] = [];
  const need = (cond: boolean, name: string, message: string) => {
    if (cond) fields.push({ name, message });
  };
  need(!details.givenName, 'givenName', 'Escribe tu nombre.');
  need(requireSurname && !details.paternalSurname, 'paternalSurname', 'Escribe el apellido paterno.');
  need(requireAll && !details.maternalSurname && !noMaternal, 'maternalSurname', 'Escribe el apellido materno o marca «No tengo apellido materno».');
  need((requireAll || Boolean(opts.requireGender)) && !details.gender, 'gender', 'Elige el género.');
  need(requireAll && !day, 'birthDay', 'Elige el día de nacimiento.');
  need(requireAll && !month, 'birthMonth', 'Elige el mes de nacimiento.');
  need(requireAll && !year, 'birthYear', 'Elige el año de nacimiento.');
  need(requireAll && !details.birthCountry, 'birthCountry', 'Elige el país de nacimiento.');
  need(requireAll && !details.birthState, 'birthState', 'Escribe o elige el estado de nacimiento.');
  need(requireAll && !details.birthCity, 'birthCity', 'Escribe o elige la ciudad de nacimiento.');
  need(requireAll && !details.address, 'address', 'Escribe la dirección.');
  need(requireAll && details.baptized === null, 'baptized', 'Indica si está bautizado(a).');
  for (const [value, name, label] of [
    [details.givenName, 'givenName', 'El nombre'],
    [details.paternalSurname, 'paternalSurname', 'El apellido paterno'],
    [details.maternalSurname, 'maternalSurname', 'El apellido materno'],
  ] as const) {
    need(Boolean(value) && !NAME_PATTERN.test(value), name, `${label} solo lleva letras (sin números ni símbolos).`);
  }
  // Fecha a medias (solo día o solo mes) cuando no es obligatoria: también se avisa.
  need(!requireAll && Boolean(day || month || year) && !birthDate, day ? (month ? 'birthYear' : 'birthMonth') : 'birthDay', 'Completa día, mes y año de nacimiento, o déjalos vacíos.');
  need(Boolean(birthDate) && day > daysIn(year, month), 'birthDay', `${MONTHS[month - 1]} de ${year} no tiene ${day} días.`);
  need(Boolean(birthDate) && birthDate! > new Date().toISOString().slice(0, 10), 'birthYear', 'La fecha de nacimiento no puede ser futura.');
  need(!PHONE.test(details.mobilePhone ?? ''), 'mobilePhone', 'Escribe el celular solo con números, espacios, +, - o paréntesis.');
  need(!PHONE.test(details.homePhone ?? ''), 'homePhone', 'Escribe el teléfono solo con números, espacios, +, - o paréntesis.');
  const error = fields.length === 0 ? null : fields.length === 1 ? fields[0]!.message : `Revisa los ${fields.length} campos marcados en rojo.`;
  return { details, error, fields };
}

/** "1990-05-03" → "3 de mayo de 1990" (sin corrimiento por zona horaria). */
export function formatBirthDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Edad cumplida a partir de "AAAA-MM-DD". */
export function ageFrom(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const now = new Date();
  let age = now.getFullYear() - y!;
  if (now.getMonth() + 1 < m! || (now.getMonth() + 1 === m! && now.getDate() < d!)) age -= 1;
  return age;
}

export const baptizedLabel = (b: boolean | null) => (b === true ? 'Sí' : b === false ? 'No' : 'Sin registrar');

/** Nombre o apellido: solo letras, y se acomodan mayúsculas al salir del campo. */
function NameField({ label, name, defaultValue, hint, disabled, optional = false }: { label: string; name: string; defaultValue?: string; hint?: string; disabled?: boolean; optional?: boolean }) {
  return (
    <Field
      label={label}
      name={name}
      required={!disabled && !optional}
      disabled={disabled}
      maxLength={100}
      autoComplete="off"
      defaultValue={defaultValue}
      hint={hint}
      pattern="[A-Za-zÀ-ÖØ-öø-ÿ' .\-]+"
      onBlur={(e) => {
        e.currentTarget.value = tidyName(e.currentTarget.value);
      }}
    />
  );
}

function Select({ id, label, name, value, onChange, children, required = true }: { id: string; label: string; name: string; value: string; onChange: (v: string) => void; children: ReactNode; required?: boolean }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} name={name} value={value} required={required} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
    </div>
  );
}

/** Campos de datos personales, iguales en el registro propio, el registro por la Iglesia y el perfil. */
export function PersonDetailsFields({ person, idPrefix = 'pd', optional = false, quick = false }: { person?: Partial<Person>; idPrefix?: string; optional?: boolean; quick?: boolean }) {
  // optional: todo opcional menos el nombre (p. ej. el pre-registro de una visita).
  // quick: solo nombre, apellido paterno y género son obligatorios (lo demás se completa después).
  const req = !optional && !quick;
  const [noMaternal, setNoMaternal] = useState(Boolean(person?.paternalSurname) && !person?.maternalSurname && Boolean(person?.birthCountry));
  const [y0, m0, d0] = (person?.birthDate ?? '').split('-');
  const [day, setDay] = useState(d0 ? String(Number(d0)) : '');
  const [month, setMonth] = useState(m0 ? String(Number(m0)) : '');
  const [year, setYear] = useState(y0 ?? '');
  const [country, setCountry] = useState(person?.birthCountry ?? 'MX');
  const [state, setState] = useState(person?.birthState ?? '');
  const [city, setCity] = useState(person?.birthCity ?? '');
  const [mexico, setMexico] = useState<Record<string, string[]> | null>(null);
  useEffect(() => {
    if (country === 'MX' && !mexico) void loadMexico().then(setMexico);
  }, [country, mexico]);

  const thisYear = new Date().getFullYear();
  const maxDay = daysIn(Number(year), Number(month) || 1);
  const states = country === 'MX' ? (mexico ? Object.keys(mexico) : []) : country === 'US' ? US_STATES : null;
  const cities = country === 'MX' && mexico && state ? (mexico[state] ?? []) : null;
  const id = (k: string) => `${idPrefix}-${k}`;

  return (
    <>
      <NameField label="Nombre(s)" name="givenName" defaultValue={person?.givenName} hint={optional ? undefined : 'Todos tus nombres, como en tu acta.'} />
      <div className="form__row">
        <NameField label="Apellido paterno" name="paternalSurname" defaultValue={person?.paternalSurname} optional={optional} />
        <NameField label="Apellido materno" name="maternalSurname" defaultValue={person?.maternalSurname} disabled={noMaternal} optional={optional || quick} />
      </div>
      <label className="check small">
        <input type="checkbox" name="noMaternal" checked={noMaternal} onChange={(e) => setNoMaternal(e.target.checked)} /> No tengo apellido materno
      </label>
      <fieldset className="choices choices--row">
        <legend>Género</legend>
        <label className="choice">
          <input type="radio" name="gender" value="femenino" defaultChecked={person?.gender === 'femenino'} required={req || quick} />
          <span>Femenino</span>
        </label>
        <label className="choice">
          <input type="radio" name="gender" value="masculino" defaultChecked={person?.gender === 'masculino'} />
          <span>Masculino</span>
        </label>
      </fieldset>

      <fieldset className="field-group">
        <legend>Fecha de nacimiento</legend>
        <div className="form__row form__row--3">
          <Select id={id('day')} required={req} label="Día" name="birthDay" value={day} onChange={setDay}>
            <option value="">Día</option>
            {Array.from({ length: maxDay }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Select>
          <Select id={id('month')} required={req} label="Mes" name="birthMonth" value={month} onChange={setMonth}>
            <option value="">Mes</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </option>
            ))}
          </Select>
          <Select id={id('year')} required={req} label="Año" name="birthYear" value={year} onChange={setYear}>
            <option value="">Año</option>
            {Array.from({ length: thisYear - 1899 }, (_, i) => thisYear - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>Lugar de nacimiento</legend>
        {person?.birthPlace && !person.birthCountry ? <p className="small muted">Antes capturado: {person.birthPlace}. Elígelo de las listas.</p> : null}
        <Select
          id={id('country')}
          required={req}
          label="País"
          name="birthCountry"
          value={country}
          onChange={(v) => {
            setCountry(v);
            setState('');
            setCity('');
          }}
        >
          <option value="">Elige el país…</option>
          {COUNTRIES_FIRST.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
          <option disabled>──────────</option>
          {COUNTRIES_REST.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
        <div className="form__row">
          {states ? (
            <Select
              id={id('state')}
              required={req}
              label="Estado"
              name="birthState"
              value={state}
              onChange={(v) => {
                setState(v);
                setCity('');
              }}
            >
              <option value="">{states.length ? 'Elige el estado…' : 'Cargando…'}</option>
              {states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          ) : (
            <Field label="Estado, provincia o departamento" name="birthState" required={req} maxLength={120} value={state} onChange={(e) => setState(e.target.value)} />
          )}
          {cities ? (
            <Select id={id('city')} required={req} label="Ciudad o municipio" name="birthCity" value={city} onChange={setCity}>
              <option value="">Elige el municipio…</option>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          ) : (
            <Field
              label="Ciudad"
              name="birthCity"
              required={req}
              maxLength={120}
              value={city}
              disabled={country === 'MX' && !state}
              placeholder={country === 'MX' && !state ? 'Primero elige el estado' : ''}
              onChange={(e) => setCity(e.target.value)}
            />
          )}
        </div>
      </fieldset>

      <div className="form__row">
        <Field label="Celular" name="mobilePhone" type="tel" inputMode="tel" autoComplete="tel" maxLength={30} placeholder="618 123 4567" defaultValue={person?.mobilePhone ?? ''} />
        <Field label="Teléfono de casa" name="homePhone" type="tel" inputMode="tel" autoComplete="off" maxLength={30} placeholder="Si tiene" defaultValue={person?.homePhone ?? ''} />
      </div>
      <div className="field">
        <label htmlFor={`${idPrefix}-address`}>Dirección</label>
        <textarea
          id={`${idPrefix}-address`}
          name="address"
          rows={2}
          maxLength={300}
          required={req}
          autoComplete="street-address"
          placeholder="Calle y número, colonia, ciudad"
          defaultValue={person?.address ?? ''}
        />
      </div>
      <fieldset className="choices choices--row">
        <legend>¿Está bautizado(a)?</legend>
        <label className="choice">
          <input type="radio" name="baptized" value="si" defaultChecked={person?.baptized === true} required={req} />
          <span>Sí</span>
        </label>
        <label className="choice">
          <input type="radio" name="baptized" value="no" defaultChecked={person?.baptized === false} />
          <span>No</span>
        </label>
      </fieldset>
    </>
  );
}
